import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { formatNaira } from "@/lib/format";
import { Building2, FileText, Footprints, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const Tile = ({ icon: Icon, label, value, sub, color = "#2ECAC8" }) => (
  <div className="s-card-light" data-testid={`ptile-${label}`}>
    <div className="flex justify-between items-start">
      <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <Icon size={16} color={color} />
    </div>
    <div style={{ fontSize: 28, color: "#0D1B2A", marginTop: 8, fontWeight: 500 }}>{value}</div>
    {sub && <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>{sub}</div>}
  </div>
);

export const PortalDashboard = () => {
  const [sites, setSites] = useState([]);
  const [reports, setReports] = useState([]);
  const [tours, setTours] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    const [s, r, t, i] = await Promise.all([
      api.get("/sites"),
      api.get("/reports"),
      api.get("/tours"),
      api.get("/invoices"),
    ]);
    setSites(s.data);
    setReports(r.data);
    setTours(t.data);
    setInvoices(i.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const incidents = useMemo(() => reports.filter(r => r.type === "INCIDENT"), [reports]);
  const outstanding = useMemo(
    () => invoices.filter(i => ["SENT", "VIEWED", "OVERDUE"].includes(i.status))
                  .reduce((s, i) => s + (i.totals?.total || 0), 0),
    [invoices]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={Building2} label="Your sites" value={sites.length} sub="Active posts" />
        <Tile icon={FileText} label="Reports filed" value={reports.length} sub="All time" />
        <Tile icon={Footprints} label="Tours done" value={tours.filter(t => t.status === "COMPLETED").length} />
        <Tile icon={AlertTriangle} label="Open incidents" value={incidents.filter(r => !r.resolved).length} color="#E63946" />
      </div>

      <div className="s-card-light">
        <div className="flex justify-between items-center mb-4">
          <div className="h-section" style={{ color: "#0D1B2A" }}>Outstanding balance</div>
          <Link to="/portal/invoices" className="btn-outline">View invoices</Link>
        </div>
        <div style={{ fontSize: 36, fontWeight: 500, color: outstanding > 0 ? "#E63946" : "#2D9E5F" }}>
          {formatNaira(outstanding)}
        </div>
        <div className="text-muted" style={{ fontSize: 13, marginTop: 6 }}>
          Pay via Paystack — card, bank transfer, or USSD.
        </div>
      </div>

      <div className="s-card-light">
        <div className="h-section mb-4" style={{ color: "#0D1B2A" }}>Recent reports</div>
        <table className="s-table s-table-light">
          <thead><tr><th>Type</th><th>Site</th><th>Date</th></tr></thead>
          <tbody>
            {reports.slice(0, 8).map(r => (
              <tr key={r.id}>
                <td>{r.type}</td>
                <td>{sites.find(s => s.id === r.site_id)?.name || "—"}</td>
                <td className="mono">{new Date(r.created_at).toLocaleDateString("en-GB")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
